(ns superadmin.users.details.view
  (:require ["@mui/material/Alert$default" :as Alert]
            ["@mui/material/Box$default" :as Box]
            ["@mui/material/Button$default" :as Button]
            ["@mui/material/Icon$default" :as Icon]
            ["@mui/material/Paper$default" :as Paper]
            ["@mui/material/Skeleton$default" :as Skeleton]
            ["@mui/material/Stack$default" :as Stack]
            ["@mui/material/Typography$default" :as Typography]
            ["@mui/x-data-grid-premium" :refer [DataGridPremium GridActionsCellItem]]
            [cljs-bean.core :refer [->clj]]
            [clojure.string :as str]
            [common.modal.view :refer [confirmation-modal defmodal modal-v2]]
            [common.queries :refer [use-mutation use-query]]
            [common.util :refer [format-iso-string]]
            [common.widgets.data-grid :as dg]
            [common.widgets.page :as page]
            [re-frame.core :as rf]
            [superadmin.users.details.ocp]
            [superadmin.users.grants.add-grant-ocp]
            [superadmin.users.queries :as q]
            [uix.core :as uix :refer [$ defui]]
            [uix.re-frame :refer [use-subscribe]]))

(defui user-info-card [{:keys [user]}]
  ($ Paper
     {:sx #js {:p 3}}
     ($ Stack {:spacing 2}
        ($ Stack {:direction "row" :spacing 1}
           ($ Typography {:variant "body2" :sx #js {:fontWeight "bold" :minWidth "120px"}}
              "ID:")
           ($ Typography {:variant "body2"}
              (or (:id user) ($ Skeleton {:width "100px"}))))

        ($ Stack {:direction "row" :spacing 1}
           ($ Typography {:variant "body2" :sx #js {:fontWeight "bold" :minWidth "120px"}}
              "Email:")
           ($ Typography {:variant "body2"}
              (or (:email user) ($ Skeleton {:width "200px"}))))

        ($ Stack {:direction "row" :spacing 1}
           ($ Typography {:variant "body2" :sx #js {:fontWeight "bold" :minWidth "120px"}}
              "Name:")
           ($ Typography {:variant "body2"}
              (or (:name user) ($ Skeleton {:width "150px"}))))

        ($ Stack {:direction "row" :spacing 1}
           ($ Typography {:variant "body2" :sx #js {:fontWeight "bold" :minWidth "120px"}}
              "Created:")
           ($ Typography {:variant "body2"}
              (if (:created-at user)
                (format-iso-string (:created-at user))
                ($ Skeleton {:width "180px"}))))

        ($ Stack {:direction "row" :spacing 1}
           ($ Typography {:variant "body2" :sx #js {:fontWeight "bold" :minWidth "120px"}}
              "Last Login:")
           ($ Typography {:variant "body2"}
              (if (:last-access user)
                (format-iso-string (:last-access user))
                "Never"))))))

(defmodal confirm-delete-grant ::confirm-delete-grant
  [props]
  ($ confirmation-modal {:title "Delete grant"
                         :confirm-details (str "The grant for role \"" (:role-name (:grant props))
                                               "\" at " (:scope-type (:grant props)) " " (:scope-id (:grant props))
                                               " will be deleted.")
                         :action-fn (:delete-fn props)}))

(defn- grants-columns [delete-grant-fn view-grant-fn]
  #js [#js {:field "role-name"
            :headerName "Role"
            :valueGetter (fn [_value row ^js _column ^js _apiRef]
                           (:role-name row))
            :flex 2}
       #js {:field "scope-type"
            :headerName "Scope Type"
            :valueGetter (fn [_value row ^js _column ^js _apiRef]
                           (:scope-type row))
            :flex 1}
       #js {:field "scope-name"
            :headerName "Scope Name"
            :valueGetter (fn [_value row ^js _column ^js _apiRef]
                           (let [scope (:scope row)
                                 scope-type (:scope-type row)]
                             (cond
                               (= scope-type "advertiser")
                               (let [advertiser-name (get-in scope [:advertiser :name])
                                     agency-name (get-in scope [:agency :name])]
                                 (if agency-name
                                   (str agency-name ": " advertiser-name)
                                   advertiser-name))
                               (= scope-type "agency")
                               (get-in scope [:agency :name])
                               :else nil)))
            :flex 2}
       #js {:field "scope-id"
            :headerName "Scope ID"
            :valueGetter (fn [_value row ^js _column ^js _apiRef]
                           (:scope-id row))
            :flex 1}
       #js {:field "created-at"
            :headerName "Created"
            :valueGetter (fn [_value row ^js _column ^js _apiRef]
                           (when-let [created (:created-at row)]
                             (format-iso-string created)))
            :flex 2}
       #js {:field "actions"
            :type "actions"
            :getActions (fn [^js params]
                          #js [($ GridActionsCellItem
                                  {:onClick #(view-grant-fn (.-row params))
                                   :icon ($ Icon "visibility")
                                   :label "View Permissions"})
                               ($ GridActionsCellItem
                                  {:onClick #(rf/dispatch
                                              [:modal/open {:id ::confirm-delete-grant
                                                            :props {:grant (.-row params)
                                                                    :delete-fn (fn []
                                                                                 (delete-grant-fn (:id (.-row params))))}}])
                                   :icon ($ Icon "delete")
                                   :label "Delete"})])}])

(def GrantsToolbar
  (uix/as-react
   (fn [props]
     (let [{:keys [user-id]} (->clj props)]
       ($ dg/grid-toolbar
          {:hide-quick-filter true
           :hide-export true
           :hide-density true
           :hide-columns true}
          ($ Button
             {:variant "contained"
              :onClick (fn []
                         (rf/dispatch [:ocp/open {:id :superadmin/add-grant
                                                  :props {:user-id (int user-id)}}]))
              :data-testid "add-grant-button"}
             "Add Grant"))))))

(defui grants-table [{:keys [user-id user-id-str]}]
  (let [{:keys [data isPending]} (use-query (q/user-grants-query {:user-id user-id}))
        {delete-grant :mutate} (use-mutation (q/delete-user-grant-mutation {:user-id user-id}))
        view-grant-fn (uix/use-callback
                       (fn [grant]
                         (rf/dispatch [:modal/open {:id ::view-grant-permissions
                                                    :props {:grant grant}}]))
                       [])]
    ($ DataGridPremium
       {:columns (grants-columns delete-grant view-grant-fn)
        :loading isPending

        :rows (into-array (or data []))
        :getRowId #(:id %)

        :disableRowSelectionOnClick true

        :pagination false
        :hideFooter true

        :slots #js {:toolbar GrantsToolbar}
        :slotProps (let [toolbar-user-id (or user-id-str (str user-id))
                         toolbar-obj (js-obj "user-id" toolbar-user-id)
                         slot-props-obj (js-obj "toolbar" toolbar-obj)]
                     slot-props-obj)

        :data-testid "table-user-grants"

        :noWrap true
        :sx #js {:maxHeight "40rem"
                 :minHeight "20rem"}})))

(defui grants-section [{:keys [user-id user-id-str]}]
  ($ Paper
     {:sx #js {:p 3}}
     ($ Typography {:variant "h2" :sx #js {:mb 2}}
        "Grants")
     ($ grants-table {:user-id user-id :user-id-str user-id-str})))

(defui permissions-display [{:keys [display-permissions]}]
  ($ Stack {:spacing 0.75}
     ($ Typography {:variant "h6"}
        "Permissions")
     (if (empty? display-permissions)
       ($ Typography {:variant "body2" :color "text.secondary"}
          "No permissions")
       ($ Stack {:spacing 0.75}
          (for [[resource actions] (sort-by first display-permissions)]
            ($ Box {:key resource
                    :sx #js {:pl 1.5
                             :py 0.25
                             :borderLeft "2px solid"
                             :borderColor "grey.60"}}
               ($ Stack {:spacing 0.5}
                  (for [[action status] (sort-by first actions)]
                    ($ Typography {:key action :variant "body2"}
                       (let [resource-name (str/capitalize (name resource))
                             action-name (str/capitalize (name action))
                             display-text (if (= status "allowed")
                                            (str resource-name ": " action-name)
                                            (str resource-name ": " action-name " (" status ")"))]
                         display-text))))))))))

(defmodal view-grant-permissions ::view-grant-permissions
  [props]
  (let [{:keys [grant]} props
        scope-name (cond
                     (= (:scope-type grant) "agency")
                     (get-in grant [:scope :agency :name])
                     (= (:scope-type grant) "advertiser")
                     (get-in grant [:scope :advertiser :name]))]
    ($ modal-v2
       {:title (str "Grant Permissions: " (:role-name grant))
        :dialog-content-props {:sx #js {:p 3}}}
       ($ Stack {:spacing 2}
          ;; Grant details section
          ($ Stack {:spacing 1}
             ($ Typography {:variant "h6"}
                "Grant Details")
             ($ Stack {:spacing 0.5}
                ($ Typography {:variant "body2"}
                   (str "Role: " (:role-name grant)))
                ($ Typography {:variant "body2"}
                   (str "Scope: " (str/capitalize (:scope-type grant)) ": "
                        (or scope-name (:scope-id grant))))
                ($ Typography {:variant "body2"}
                   (str "Created: " (when-let [created (:created-at grant)]
                                      (-> created js/Date. str))))))

          ;; Info alert
          ($ Alert {:severity "info" :sx #js {:mt 2 :mb 2}}
             "This shows permissions from this single grant only. The user's effective permissions may include additional permissions from other grants.")

          ;; Permissions section (from grant row; same map shape as role catalog)
          ($ permissions-display {:display-permissions (:permissions grant)})))))

(defui user-details-page []
  (let [path-params (use-subscribe [:router/path-params])
        user-id-str (:user-id path-params)
        user-id (some-> user-id-str js/parseInt)
        {:keys [data isPending]} (use-query (q/user-query {:user-id user-id}))]
    ($ page/page
       ($ page/page-header {:title (if isPending
                                     ($ Skeleton {:width "200px"})
                                     (str "User: " (:email data)))})
       ($ page/content-padding
          ($ Stack {:spacing 3}
             ($ user-info-card {:user data})
             ($ grants-section {:user-id user-id :user-id-str user-id-str}))))))
